import { useMemo } from "react";
import { getDateRange } from "../../utils/strategyPerformanceUtils";
import { backtestSummaryFromNav } from "./backtestMetrics";

/**
 * Custom hook to calculate strategy performance metrics
 */
export const useStrategyMetrics = ({
  strategyVal,
  historicalSignalSet,
  period,
  customFromDate,
  customToDate,
  appliedCustomFromDate,
  appliedCustomToDate,
  chartData,
  dateRangeLimits,
}) => {
  // Calculate backtest metrics from NAV series using backtestSummaryFromNav
  // ALWAYS use original, non-rebased strategyNavSeries for metrics (independent of period filters)
  const calculatedMetrics = useMemo(() => {
    if (!strategyVal || !historicalSignalSet?.navs?.strategyNavSeries) {
      return null;
    }

    try {
      // Get date range based on period selection
      // Use applied dates for Custom period, input dates for other periods
      const fromDate =
        period === "Custom" ? appliedCustomFromDate : customFromDate;
      const toDate = period === "Custom" ? appliedCustomToDate : customToDate;
      const dateRange = getDateRange(period, fromDate, toDate);
      const hasPeriodFilter =
        dateRange && dateRange.startDate && dateRange.endDate;

      // ALWAYS use original, non-rebased strategyNavSeries for metrics calculation
      const strategyNavSeriesData = historicalSignalSet.navs.strategyNavSeries;
      if (!strategyNavSeriesData) return null;

      // Convert NAV series object to Map
      const navSeries = new Map(
        Object.entries(strategyNavSeriesData).map(([date, nav]) => [date, nav])
      );

      let startDate, endDate;

      if (hasPeriodFilter) {
        // For period filters: use the filtered date range but with original NAV values (not rebased)
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
      } else {
        // For "Inception to date": use original strategyNavSeries with its first and last dates
        const navDates = Array.from(navSeries.keys()).sort((a, b) => a.localeCompare(b));
        startDate = navDates.length > 0 ? navDates[0] : null;
        endDate = navDates.length > 0 ? navDates[navDates.length - 1] : null;
      }

      if (!startDate || !endDate || !navSeries || navSeries.size === 0)
        return null;

      // Optional configuration
      const options = {
        tradingDays: 252,
        riskFreeRateAnnual: 0.02, // 2% annual risk-free rate
        ddof: 1,
        sortinoThreshold: 0,
        ignoreNonTradingDays: true,
      };

      
      const result = backtestSummaryFromNav(navSeries, startDate, endDate, options);
    
      return result;
    } catch (error) {
      console.error("Error calculating backtest metrics:", error);
      return null;
    }
  }, [
    strategyVal,
    historicalSignalSet,
    period,
    customFromDate,
    customToDate,
    appliedCustomFromDate,
    appliedCustomToDate,
    dateRangeLimits?.minDate,
    dateRangeLimits?.maxDate,
  ]);

  // Calculate metric data for display
  const metricData = useMemo(() => {
    if (!strategyVal) {
      return {
        backtestPeriod: "—",
        days: "—",
        totalReturn: "0%",
        annualized: "0% annualized",
        sharpeRatio: "0",
        maxDrawdown: "0%",
        drawdownDuration: "0 days duration",
      };
    }

    // Use calculatedMetrics if available (calculated from selected benchmark/period)
    if (calculatedMetrics) {
      const backtest = calculatedMetrics.backtest || {};
      const startDate = backtest.effectiveStartDate || backtest.startDate || "";
      const endDate = backtest.effectiveEndDate || backtest.endDate || "";
      const backtestPeriod =
        startDate && endDate ? `${startDate} to ${endDate}` : "—";

      // Calculate days
      let days = "—";
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const years = (diffDays / 365.25).toFixed(2);
        days = `${diffDays} days (${years} years)`;
      }

      // Format total return
      const totalReturn =
        calculatedMetrics.totalTimeWeightedReturnPct !== undefined &&
        !isNaN(calculatedMetrics.totalTimeWeightedReturnPct)
          ? `${calculatedMetrics.totalTimeWeightedReturnPct.toFixed(2)}%`
          : "0%";

      // Format annualized return
      const annualized =
        calculatedMetrics.annualizedReturnPct !== undefined &&
        !isNaN(calculatedMetrics.annualizedReturnPct)
          ? `${calculatedMetrics.annualizedReturnPct.toFixed(2)}% annualized`
          : "0% annualized";

      // Format Sharpe ratio
      const sharpeRatio =
        calculatedMetrics.sharpe !== undefined &&
        !isNaN(calculatedMetrics.sharpe)
          ? calculatedMetrics.sharpe.toFixed(2)
          : "0";

      // Format max drawdown
      const worstDrawdown = calculatedMetrics.worstDrawdown || {};
      const maxDrawdown =
        worstDrawdown.drawdownPct !== undefined &&
        !isNaN(worstDrawdown.drawdownPct)
          ? `${worstDrawdown.drawdownPct.toFixed(2)}%`
          : "0%";

      // Format drawdown duration
      const drawdownDuration =
        worstDrawdown.daysFromStartToEnd !== undefined &&
        worstDrawdown.daysFromStartToEnd !== null
          ? `${worstDrawdown.daysFromStartToEnd} days duration`
          : "0 days duration";

      return {
        backtestPeriod,
        days,
        totalReturn,
        annualized,
        sharpeRatio,
        maxDrawdown,
        drawdownDuration,
      };
    }

    // Fallback to backend data if calculatedMetrics is not available
    if (!historicalSignalSet?.backtestSummary) {
      return {
        backtestPeriod: "—",
        days: "—",
        totalReturn: "0%",
        annualized: "0% annualized",
        sharpeRatio: "0",
        maxDrawdown: "0%",
        drawdownDuration: "0 days duration",
      };
    }

    const summary = historicalSignalSet.backtestSummary;
    const backtest = summary.backtest || {};

    // Format dates
    const startDate = backtest.startDate || backtest.effectiveStartDate || "";
    const endDate = backtest.endDate || backtest.effectiveEndDate || "";
    const backtestPeriod =
      startDate && endDate ? `${startDate} to ${endDate}` : "—";

    // Calculate days
    let days = "—";
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const years = (diffDays / 365.25).toFixed(2);
      days = `${diffDays} days (${years} years)`;
    }

    // Format total return
    const totalReturn =
      summary.totalTimeWeightedReturnPct !== undefined
        ? `${summary.totalTimeWeightedReturnPct.toFixed(2)}%`
        : "0%";

    // Format annualized return
    const annualized =
      summary.annualizedReturnPct !== undefined
        ? `${summary.annualizedReturnPct.toFixed(2)}% annualized`
        : "0% annualized";

    // Format Sharpe ratio
    const sharpeRatio =
      summary.sharpe !== undefined ? summary.sharpe.toFixed(2) : "0";

    // Format max drawdown
    const worstDrawdown = summary.worstDrawdown || {};
    const maxDrawdown =
      worstDrawdown.drawdownPct !== undefined
        ? `${worstDrawdown.drawdownPct.toFixed(2)}%`
        : "0%";

    // Format drawdown duration
    const drawdownDuration =
      worstDrawdown.daysFromStartToEnd !== undefined
        ? `${worstDrawdown.daysFromStartToEnd} days duration`
        : "0 days duration";

    return {
      backtestPeriod,
      days,
      totalReturn,
      annualized,
      sharpeRatio,
      maxDrawdown,
      drawdownDuration,
    };
  }, [strategyVal, calculatedMetrics, historicalSignalSet]);

  return { calculatedMetrics, metricData };
};

